import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useCustomerAuth } from '../lib/customerAuth';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Phone, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

import { errMsg } from '../lib/errors';
export default function LoginModal({ open, onOpenChange, onSuccess }) {
    const nav = useNavigate();
    const { login } = useCustomerAuth();
    const [step, setStep] = useState('mobile'); // mobile | otp | register
    const [mobile, setMobile] = useState('');
    const [otp, setOtp] = useState('');
    const [devOtp, setDevOtp] = useState('');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [pincode, setPincode] = useState('');
    const [postOffices, setPostOffices] = useState([]);
    const [selectedPO, setSelectedPO] = useState(null);
    const [loading, setLoading] = useState(false);

    const reset = () => {
        setStep('mobile'); setMobile(''); setOtp(''); setDevOtp('');
        setName(''); setEmail(''); setPincode(''); setPostOffices([]); setSelectedPO(null);
    };

    const handleClose = (v) => { if (!v) reset(); onOpenChange(v); };

    const sendOtp = async () => {
        if (!/^\+?[0-9]{10,14}$/.test(mobile)) { toast.error('Enter a valid mobile number'); return; }
        setLoading(true);
        try {
            const r = await api.post('/auth/customer/send-otp', { mobile });
            setDevOtp(r.data.dev_otp || '');
            toast.success(`OTP sent to ${mobile}${r.data.dev_otp ? ` (dev: ${r.data.dev_otp})` : ''}`);
            setStep('otp');
        } catch (e) { toast.error(errMsg(e, 'Failed to send OTP')); }
        finally { setLoading(false); }
    };

    const verifyOtp = async () => {
        setLoading(true);
        try {
            const r = await api.post('/auth/customer/verify-otp', { mobile, otp });
            if (r.data.new_user) {
                setStep('register');
            } else {
                login(r.data.token, r.data.customer);
                toast.success('Welcome back!');
                onSuccess && onSuccess();
                handleClose(false);
            }
        } catch (e) { toast.error(errMsg(e, 'OTP verification failed')); }
        finally { setLoading(false); }
    };

    const lookupPincode = async () => {
        if (!/^\d{6}$/.test(pincode)) { toast.error('Enter valid 6-digit pincode'); return; }
        setLoading(true);
        try {
            const r = await api.get(`/shipping/pincode/${pincode}`);
            if (r.data.success && r.data.post_offices.length > 0) {
                setPostOffices(r.data.post_offices);
                setSelectedPO(r.data.post_offices[0]);
                toast.success(`Found ${r.data.post_offices.length} location(s)`);
            } else {
                toast.error('No locations found for this pincode');
            }
        } catch (e) { toast.error('Pincode lookup failed'); }
        finally { setLoading(false); }
    };

    const doRegister = async () => {
        if (!name.trim()) { toast.error('Please enter your name'); return; }
        if (!selectedPO) { toast.error('Please select your location'); return; }
        setLoading(true);
        try {
            const location = {
                pincode: selectedPO.pincode, post_office: selectedPO.post_office,
                area: selectedPO.post_office, district: selectedPO.district,
                state: selectedPO.state, country: selectedPO.country || 'India',
            };
            const r = await api.post('/auth/customer/register-retail', {
                name, mobile, email: email || null, location
            });
            login(r.data.token, r.data.customer);
            toast.success(`Welcome to Dharmik Gardens, ${name}!`);
            onSuccess && onSuccess();
            handleClose(false);
        } catch (e) { toast.error(errMsg(e, 'Registration failed')); }
        finally { setLoading(false); }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>
                        {step === 'mobile' && 'Sign in with Mobile'}
                        {step === 'otp' && 'Enter OTP'}
                        {step === 'register' && 'Complete your profile'}
                    </DialogTitle>
                </DialogHeader>
                {step === 'mobile' && (
                    <div className="space-y-3" data-testid="login-mobile-step">
                        <div>
                            <Label>Mobile Number</Label>
                            <div className="relative">
                                <Phone className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                                <Input value={mobile} onChange={e => setMobile(e.target.value)} placeholder="+919876543210" className="pl-9" data-testid="customer-mobile-input" />
                            </div>
                            <div className="text-xs text-neutral-500 mt-1">We’ll send you a 6-digit OTP to verify.</div>
                        </div>
                        <Button className="w-full" onClick={sendOtp} disabled={loading} data-testid="send-otp-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>
                            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Send OTP
                        </Button>
                        <div className="text-center text-xs text-neutral-500 pt-2 border-t">
                            Business customer? <a href="/wholesale" className="text-blue-700 font-medium">Apply for wholesale</a>
                        </div>
                    </div>
                )}
                {step === 'otp' && (
                    <div className="space-y-3" data-testid="login-otp-step">
                        <div className="text-sm text-neutral-600">OTP sent to <span className="font-medium text-neutral-900">{mobile}</span></div>
                        {devOtp && (
                            <div className="p-3 bg-blue-50 text-blue-900 text-xs rounded-md">
                                <span className="font-semibold">Demo OTP:</span> {devOtp}
                            </div>
                        )}
                        <div>
                            <Label>6-digit OTP</Label>
                            <div className="relative">
                                <KeyRound className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                                <Input value={otp} onChange={e => setOtp(e.target.value)} maxLength={6} className="pl-9 tracking-widest text-lg font-mono" data-testid="otp-input" />
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setStep('mobile')}>Change Number</Button>
                            <Button className="flex-1" onClick={verifyOtp} disabled={loading || otp.length !== 6} data-testid="verify-otp-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>
                                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Verify &amp; Continue
                            </Button>
                        </div>
                    </div>
                )}
                {step === 'register' && (
                    <div className="space-y-3" data-testid="login-register-step">
                        <div><Label>Full Name</Label><Input value={name} onChange={e => setName(e.target.value)} data-testid="register-name-input" /></div>
                        <div><Label>Email (optional)</Label><Input type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="register-email-input" /></div>
                        <div>
                            <Label>Pincode</Label>
                            <div className="flex gap-2">
                                <Input value={pincode} onChange={e => setPincode(e.target.value)} placeholder="6-digit pincode" data-testid="register-pincode-input" />
                                <Button variant="outline" onClick={lookupPincode} disabled={loading} data-testid="pincode-lookup-button">Lookup</Button>
                            </div>
                        </div>
                        {postOffices.length > 0 && (
                            <div>
                                <Label>Select Area</Label>
                                <select className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                                    value={selectedPO?.post_office || ''}
                                    onChange={e => setSelectedPO(postOffices.find(p => p.post_office === e.target.value))}
                                    data-testid="register-area-select">
                                    {postOffices.map(po => (
                                        <option key={po.post_office} value={po.post_office}>{po.post_office}, {po.district}, {po.state}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <Button className="w-full" onClick={doRegister} disabled={loading} data-testid="complete-register-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>
                            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Complete Sign Up
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
